// Pure queue state + sequential runner for multi-video upload (VideoFormPage,
// create mode). Kept dependency-free (no React, no api client) so it's testable
// without mounting the page — see openspec/changes/video-multi-upload-queue.
export type QueueItemStatus = "pending" | "uploading" | "processing" | "done" | "error"

export type QueueItem = {
  id: string
  file: File
  title: string
  status: QueueItemStatus
  progress: number | null
  error?: string
  videoId?: string
  uploadedToR2?: boolean
}

export function createQueueItems(files: File[], titleFromFileName: (name: string) => string): QueueItem[] {
  return files.map((file, i) => ({
    id: `${Date.now()}-${i}-${file.name}`,
    file,
    title: titleFromFileName(file.name),
    status: "pending",
    progress: null,
  }))
}

export function patchItem(queue: QueueItem[], id: string, patch: Partial<QueueItem>): QueueItem[] {
  return queue.map((item) => (item.id === id ? { ...item, ...patch } : item))
}

export function removeItem(queue: QueueItem[], id: string): QueueItem[] {
  return queue.filter((item) => item.id !== id)
}

export function nextPending(queue: QueueItem[]): QueueItem | undefined {
  return queue.find((item) => item.status === "pending")
}

export function isQueueActive(queue: QueueItem[]): boolean {
  return queue.some((item) => item.status === "uploading" || item.status === "processing")
}

export type UploadCallbacks = {
  onProgress: (percent: number) => void
  onProcessing: () => void
}

export type UploadFn = (item: QueueItem, callbacks: UploadCallbacks) => Promise<{ videoId: string }>

/**
 * Drives the queue one item at a time: picks the next `pending` item, marks it
 * `uploading`, calls `uploadOne`, and on settle marks it `done`/`error` before
 * advancing to the next pending item. Returns once no pending item remains or
 * another run is already active (re-entrancy guard, so calling this again from
 * "add more files" or "retry" is always safe).
 */
export async function runQueue(
  getQueue: () => QueueItem[],
  setQueue: (updater: (prev: QueueItem[]) => QueueItem[]) => void,
  uploadOne: UploadFn,
): Promise<void> {
  if (isQueueActive(getQueue())) return

  for (;;) {
    const item = nextPending(getQueue())
    if (!item) return

    setQueue((q) => patchItem(q, item.id, { status: "uploading", progress: 0, error: undefined }))
    try {
      const { videoId } = await uploadOne(item, {
        onProgress: (percent) => setQueue((q) => patchItem(q, item.id, { progress: percent })),
        onProcessing: () => setQueue((q) => patchItem(q, item.id, { status: "processing", progress: null })),
      })
      setQueue((q) => patchItem(q, item.id, { status: "done", progress: 100, videoId }))
    } catch (err) {
      setQueue((q) =>
        patchItem(q, item.id, {
          status: "error",
          error: err instanceof Error ? err.message : String(err),
        }),
      )
      // Don't stop the whole queue for one bad file — move on to the next pending item.
    }
  }
}
